// ─────────────────────────────────────────────────────────────────────────────
// challanDocument.js
// Single source of truth for the printed fee-challan layout (bank-style, three
// copies per sheet). Both the on-screen preview and the print window render the
// same HTML so what the user sees is exactly what comes out of the printer.
// ─────────────────────────────────────────────────────────────────────────────

import {
  CHALLAN_BANK_GRACE_DAYS,
  CHALLAN_BANK_BRANCH,
  CHALLAN_BANK_IBAN,
  CHALLAN_BANK_NAME,
  CHALLAN_COPIES,
  CHALLAN_LATE_FEE,
  CHALLAN_SCHOOL_NAME,
} from "./feeConstants";

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ONES = ["", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN",
  "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN", "EIGHTEEN", "NINETEEN"];
const TENS = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtAmount = (value) => Number(value || 0).toLocaleString("en-PK");

const fmtShortDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}-${SHORT_MONTHS[date.getMonth()]}-${String(date.getFullYear()).slice(-2)}`;
};

const belowThousandToWords = (n) => {
  if (n < 20) return ONES[n];
  if (n < 100) {
    const tens = TENS[Math.floor(n / 10)];
    const ones = ONES[n % 10];
    return ones ? `${tens}-${ones}` : tens;
  }
  const hundreds = `${ONES[Math.floor(n / 100)]} HUNDRED`;
  const rest = n % 100;
  return rest ? `${hundreds} ${belowThousandToWords(rest)}` : hundreds;
};

/** Converts a rupee amount into the words line printed under the totals. */
export const amountInWords = (amount) => {
  const rupees = Math.max(0, Math.round(Number(amount) || 0));
  if (rupees === 0) return "ZERO RUPEES ONLY.";

  // Pakistani numbering: crore, lakh, thousand, then the last three digits.
  const units = [
    { value: 10000000, label: "CRORE" },
    { value: 100000, label: "LAKH" },
    { value: 1000, label: "THOUSAND" },
  ];

  let remaining = rupees;
  const parts = [];
  units.forEach(({ value, label }) => {
    const count = Math.floor(remaining / value);
    if (count > 0) {
      parts.push(`${belowThousandToWords(count)} ${label}`);
      remaining %= value;
    }
  });
  if (remaining > 0) parts.push(belowThousandToWords(remaining));

  return `${parts.join(" ")} RUPEES ONLY.`;
};

/**
 * Normalises a student + fee assignment + stored challan into the exact set of
 * values printed on the challan, so every caller shows identical numbers.
 */
export const buildChallanData = ({ student = {}, feeStructure = {}, challan = null, previousDues = 0 }) => {
  const rawComponents = challan?.components || feeStructure.components || [];
  const components = rawComponents.map((component) => ({
    label: component.componentLabel || component.componentId || "Fee",
    amount: Number(component.amount) || 0,
  }));

  const subtotal = components.reduce((sum, component) => sum + component.amount, 0);
  const discount = Number(challan?.concessionAmount ?? feeStructure.concessionAmount ?? 0) || 0;
  // Carried-forward dues can already be billed as a component; only add them to
  // the Arrears row when they are not, so the total is never counted twice.
  const duesBilledAsComponent = rawComponents.some(
    (component) => String(component.componentId || "").toLowerCase() === "previous_dues",
  );
  const arrears = duesBilledAsComponent ? 0 : Number(previousDues) || 0;
  const totalSchoolFee = Math.max(0, subtotal - discount) + arrears;
  const lateFee = CHALLAN_LATE_FEE;

  const monthValue = String(challan?.month || feeStructure.month || "");
  const [yearPart, monthPart] = monthValue.split("-");
  const year = Number(yearPart);
  const monthIndex = monthPart ? Number(monthPart) - 1 : NaN;
  const hasPeriod = Number.isFinite(year) && Number.isFinite(monthIndex) && monthIndex >= 0;

  const periodStart = hasPeriod ? new Date(year, monthIndex, 1) : null;
  const periodEnd = hasPeriod ? new Date(year, monthIndex + 1, 0) : null;

  const dueDay = Number(challan?.dueDate || feeStructure.dueDate || 10) || 10;
  const dueDate = hasPeriod ? new Date(year, monthIndex, dueDay) : null;
  const bankLastDate = dueDate
    ? new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate() + CHALLAN_BANK_GRACE_DAYS)
    : null;

  return {
    schoolName: student.campus_name || student.campusName || CHALLAN_SCHOOL_NAME,
    bankName: CHALLAN_BANK_NAME,
    bankBranch: CHALLAN_BANK_BRANCH,
    bankIban: CHALLAN_BANK_IBAN,
    challanNumber: challan?.challanNumber || "—",
    admissionNumber: student.student_id || student.rollNo || student.id || "—",
    studentName: student.name || "—",
    className: (student.class || student.class_name || "—").replace(/Grade/i, "Class").trim(),
    section: (student.section || student.class_section || "—").replace(/Section\s*/i, "").trim() || "—",
    session:
      challan?.session ||
      feeStructure.session ||
      student.academic_session ||
      challan?.academicYear ||
      feeStructure.academicYear ||
      "",
    categoryLabel: challan?.categoryLabel || feeStructure.categoryLabel || "Fee",
    monthText: hasPeriod ? `${SHORT_MONTHS[monthIndex]} ${year}` : "—",
    periodText: periodStart && periodEnd ? `${fmtShortDate(periodStart)} To:${fmtShortDate(periodEnd)}` : "—",
    dueDateText: fmtShortDate(dueDate),
    bankLastDateText: fmtShortDate(bankLastDate),
    components,
    arrears,
    discount,
    totalSchoolFee,
    lateFee,
    totalAfterDueDate: totalSchoolFee + lateFee,
    amountWords: amountInWords(totalSchoolFee),
  };
};

/** CSS shared by the preview and the print window. */
export const challanStyles = `
  .chl-sheet { display: flex; gap: 6px; padding: 6px; background: #ffffff; }
  .chl-copy { flex: 1; min-width: 0; border: 1px solid #111111; padding: 8px 9px 14px; font-family: Arial, Helvetica, sans-serif; color: #111111; }
  .chl-head { border: 1px solid #111111; padding: 5px 6px; text-align: center; }
  .chl-school { font-size: 9.5px; font-weight: 700; }
  .chl-bank { margin-top: 5px; padding-top: 4px; border-top: 1px solid #111111; font-size: 9px; font-weight: 700; }
  .chl-inst { margin-top: 7px; border: 1px solid #111111; padding: 5px 6px; font-size: 7.5px; line-height: 1.45; }
  .chl-inst-title { font-weight: 700; font-size: 8px; }
  .chl-inst-row { display: flex; justify-content: space-between; gap: 6px; }
  .chl-inst-num { font-weight: 700; text-decoration: underline; white-space: nowrap; }
  .chl-number { margin-top: 7px; font-size: 11px; font-weight: 700; text-align: center; }
  .chl-meta { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 8.5px; }
  .chl-meta td { padding: 3px 2px; border-bottom: 1px solid #111111; vertical-align: bottom; }
  .chl-meta .chl-key { font-weight: 700; white-space: nowrap; width: 1%; }
  .chl-meta .chl-val { font-weight: 600; }
  .chl-table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 8.5px; }
  .chl-table th, .chl-table td { border: 1px solid #111111; padding: 3px 5px; }
  .chl-table th { font-size: 8px; font-weight: 700; }
  .chl-table .chl-amt { text-align: right; white-space: nowrap; }
  .chl-table .chl-strong td { font-weight: 700; }
  .chl-words { margin-top: 6px; font-size: 7.5px; font-weight: 700; }
  .chl-footer { margin-top: 58px; display: flex; justify-content: space-between; align-items: flex-end; font-size: 8.5px; font-weight: 600; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .chl-sheet { page-break-after: always; }
    .chl-sheet:last-child { page-break-after: auto; }
    @page { size: A4 landscape; margin: 6mm; }
  }
`;

const metaRowsHtml = (data) => `
  <tr>
    <td class="chl-key">Admission #:</td>
    <td class="chl-val">${escapeHtml(data.admissionNumber)}</td>
    <td class="chl-key">Class:</td>
    <td class="chl-val">${escapeHtml(data.className)}</td>
  </tr>
  <tr>
    <td class="chl-key">Name:</td>
    <td class="chl-val" colspan="3">${escapeHtml(data.studentName)}</td>
  </tr>
  <tr>
    <td class="chl-key">Section:</td>
    <td class="chl-val" colspan="3">${escapeHtml(data.section)}${data.session ? ` &nbsp; <span style="font-weight:600">${escapeHtml(data.session)}</span>` : ""}</td>
  </tr>
  <tr>
    <td class="chl-key">Month:</td>
    <td class="chl-val" colspan="3">${escapeHtml(data.monthText)}</td>
  </tr>
  <tr>
    <td class="chl-key">Due Date:</td>
    <td class="chl-val" colspan="3">${escapeHtml(data.dueDateText)}</td>
  </tr>
`;

const amountRowsHtml = (data) => {
  const componentRows = data.components
    .map(
      (component) => `
        <tr>
          <td>${escapeHtml(component.label)}</td>
          <td class="chl-amt">${fmtAmount(component.amount)}</td>
        </tr>`,
    )
    .join("");

  return `
    ${componentRows}
    <tr>
      <td>Arrears</td>
      <td class="chl-amt">${fmtAmount(data.arrears)}</td>
    </tr>
    <tr>
      <td>Discount</td>
      <td class="chl-amt">${fmtAmount(data.discount)}</td>
    </tr>
    <tr class="chl-strong">
      <td>Total School Fee</td>
      <td class="chl-amt">${fmtAmount(data.totalSchoolFee)}</td>
    </tr>
    <tr class="chl-strong">
      <td>Challan Total: (Due Date)</td>
      <td class="chl-amt">${fmtAmount(data.totalSchoolFee)}</td>
    </tr>
    <tr>
      <td>Late Fee (After Due Date)</td>
      <td class="chl-amt">${fmtAmount(data.lateFee)}</td>
    </tr>
    <tr class="chl-strong">
      <td>Challan Total (After Due Date)</td>
      <td class="chl-amt">${fmtAmount(data.totalAfterDueDate)}</td>
    </tr>
    <tr>
      <td>Bank Don't Accept Challan After This Date</td>
      <td class="chl-amt">${escapeHtml(data.bankLastDateText)}</td>
    </tr>
  `;
};

/** One challan copy (Bank / School / Student). */
export const renderChallanCopyHtml = (data, copyLabel) => `
  <section class="chl-copy">
    <div class="chl-head">
      <div class="chl-school">${escapeHtml(data.schoolName)}</div>
      <div class="chl-bank">${escapeHtml(data.bankName)}</div>
    </div>

    <div class="chl-inst">
      <div class="chl-inst-title">For 1Link Bank Transaction</div>
      <div class="chl-inst-row">
        <span>Bank Name</span>
        <span class="chl-inst-num">${escapeHtml(data.bankName)}</span>
      </div>
      <div class="chl-inst-row">
        <span>Branch Name</span>
        <span class="chl-inst-num">${escapeHtml(data.bankBranch)}</span>
      </div>
      <div class="chl-inst-row">
        <span>IBAN No.</span>
        <span class="chl-inst-num">${escapeHtml(data.bankIban)}</span>
      </div>
      <div class="chl-inst-row">
        <span>Challan No.</span>
        <span class="chl-inst-num">${escapeHtml(data.challanNumber)}</span>
      </div>
    </div>

    <div class="chl-number">Challan #: &nbsp; ${escapeHtml(data.challanNumber)}</div>

    <table class="chl-meta"><tbody>${metaRowsHtml(data)}</tbody></table>

    <table class="chl-table">
      <thead>
        <tr>
          <th>Description</th>
          <th class="chl-amt">Amount</th>
        </tr>
      </thead>
      <tbody>${amountRowsHtml(data)}</tbody>
    </table>

    <div class="chl-words">${escapeHtml(data.amountWords)}</div>

    <div class="chl-footer">
      <span>${escapeHtml(copyLabel)}</span>
      <span>Accounts Officer</span>
    </div>
  </section>
`;

/** One printed sheet holding all three copies of a single challan. */
export const renderChallanSheetHtml = (data) =>
  `<div class="chl-sheet">${CHALLAN_COPIES.map((label) => renderChallanCopyHtml(data, label)).join("")}</div>`;

/** Opens the browser print dialog for one or more challans. */
export const printChallanSheets = (dataList, title = "Fee Challan") => {
  const sheets = (Array.isArray(dataList) ? dataList : [dataList]).filter(Boolean);
  if (!sheets.length) return;

  const win = window.open("", "_blank");
  if (!win) return;

  win.document.write(`<!DOCTYPE html><html><head><title>${escapeHtml(title)}</title><style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #ffffff; font-family: Arial, Helvetica, sans-serif; }
    ${challanStyles}
  </style></head><body>${sheets.map(renderChallanSheetHtml).join("")}</body></html>`);
  win.document.close();
  win.focus();
  win.print();
};
